import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { Trash2, AlertCircle } from 'react-feather';
import { isEmpty } from 'lodash/fp';
import { Icon } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import { routes, VIDEO_CONFERENCE_TYPES } from 'constants/config';
import RadioFieldGroup from 'components/shared/RadioFieldGroup';
import RadioField from 'components/shared/RadioFieldGroup/RadioField';
import ConnectZoomButton from 'components/shared/ConnectZoomButton';
import { isValidURL } from 'utils/string_utils';
import request from 'utils/request_ajax';
import FeatureFlag from 'components/shared/FeatureFlag';

function VideoConference({
  onChange,
  currentUser,
  currentCompany,
  videoConferencing = { provider: null, url: null },
  disabled,
  startTime,
  timeZone,
  timeZones,
  sessionId,
}) {
  const { OTHER, NONE, ZOOM } = VIDEO_CONFERENCE_TYPES;

  const [videoConference, setVideoConference] = useState(
    videoConferencing.provider || VIDEO_CONFERENCE_TYPES.NONE,
  );
  const [otherUrl, setOtherUrl] = useState(videoConferencing.url || '');
  const [zoomUrl, setZoomUrl] = useState(
    videoConferencing.provider === ZOOM && videoConferencing.url
      ? videoConferencing.url
      : null,
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [integrationsConnected, setIntegrationsConnected] = useState({});

  useEffect(() => {
    request
      .get(routes.USERS_INTEGRATIONS(currentUser?.id))
      .then(data => {
        setIntegrationsConnected(data);
        setLoading(false);
      })
      .catch(err => setError(err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [isZoomConnected, setIsZoomConnected] = useState(false);
  const [isLicensed, setIsLicensed] = useState(false);

  const integrationCheck = (collection, provider) => {
    return collection.some(integration => integration.provider === provider);
  };

  useEffect(() => {
    if (!loading && !error) {
      setIsZoomConnected(
        integrationCheck(integrationsConnected, VIDEO_CONFERENCE_TYPES.ZOOM),
      );
      if (
        isZoomConnected &&
        integrationsConnected.find(
          integration => integration.provider === VIDEO_CONFERENCE_TYPES.ZOOM,
        ).licensed === true
      ) {
        setIsLicensed(true);
      }
    }
  }, [integrationsConnected, isZoomConnected, loading, error]);

  const activeVideoConferenceUrl = useMemo(() => {
    if (videoConference === OTHER) {
      return otherUrl;
    }
    if (videoConference === ZOOM) {
      return zoomUrl;
    }
    return null;
  }, [videoConference, OTHER, ZOOM, otherUrl, zoomUrl]);

  useEffect(
    () =>
      onChange({
        type: videoConference,
        url: activeVideoConferenceUrl,
        id: videoConferencing.id,
        host_id: videoConferencing.host_id,
        licensed: isLicensed,
        sessionId,
      }),
    [
      videoConference,
      videoConferencing.id,
      videoConferencing.host_id,
      otherUrl,
      onChange,
      zoomUrl,
      activeVideoConferenceUrl,
      isLicensed,
      sessionId,
    ],
  );

  const handleTypeChange = ({ target }) => {
    const { value, name } = target;
    if (name !== 'zoomVideoConferenceUrl') setVideoConference(value);

    if (otherUrl !== '' || value !== OTHER) {
      setOtherUrl('');
    }
  };

  const getRequiredText = () => {
    const { INSTRUX_SMALL_SEMIBOLD, INSTRUX_SMALL_SEMIBOLD_ERROR } =
      TEXT_VARIANTS;

    const variant = !isValidURL(otherUrl)
      ? INSTRUX_SMALL_SEMIBOLD_ERROR
      : INSTRUX_SMALL_SEMIBOLD;

    let requiredText = t('common.required');

    if (!isEmpty(otherUrl) && !isValidURL(otherUrl)) {
      requiredText = t('in_person_training.new_event.invalid_url');
    }
    return (
      <div className="pb-6">
        <Text variant={variant}>
          <Icon name="required" /> {requiredText}
        </Text>
      </div>
    );
  };

  const isStartBeforeCurrent = startTime.isBefore(moment());
  const isTimeZoneSelected = timeZone !== '';

  const tooltip = (
    <span
      role="button"
      className="tooltip-text"
      data-toggle="tooltip"
      data-placement="top"
      title={t('in_person_training.new_event.zoom_tooltip')}
      aria-label={t('in_person_training.new_event.zoom_meeting_paid')}
      tabIndex="0"
    >
      {t('in_person_training.new_event.zoom_meeting_paid')}
    </span>
  );

  const disabledTooltip = (
    <span
      role="button"
      className="tooltip-text"
      aria-disabled="true"
      aria-label={t('in_person_training.new_event.zoom_meeting_paid')}
      tabIndex="0"
    >
      {t('in_person_training.new_event.zoom_meeting_paid')}
    </span>
  );

  const zoomOption = (
    <RadioField
      value={ZOOM}
      disabled={isStartBeforeCurrent || !isTimeZoneSelected}
    >
      {t('in_person_training.new_event.zoom_meeting')}
      <FeatureFlag features="video_conferencing_attendance_tracking">
        <Text variant={TEXT_VARIANTS.BODY_LIGHT} component="div">
          <span className="fz-14">
            {t('in_person_training.new_event.zoom_meeting_description')}{' '}
            {isStartBeforeCurrent || !isTimeZoneSelected
              ? disabledTooltip
              : tooltip}
          </span>
        </Text>
      </FeatureFlag>
    </RadioField>
  );

  const getZoomOption = () => {
    if (isZoomConnected && isStartBeforeCurrent) {
      return (
        <span
          data-placement="left"
          data-toggle="tooltip"
          title={t('in_person_training.new_event.zoom_past_date_tip')}
        >
          {zoomOption}
        </span>
      );
    }

    if (isZoomConnected) {
      return zoomOption;
    }

    return (
      <ConnectZoomButton
        currentUser={currentUser}
        currentCompany={currentCompany}
        showTooltip
        disabled={disabled}
        loading={loading}
      />
    );
  };

  const ZoomMeetingUrl = () => {
    if (zoomUrl === null || videoConference !== ZOOM || !isZoomConnected) {
      return null;
    }
    return (
      <div className="d-f ai-c pt-4 pb-8">
        <button
          type="button"
          className="u-resetBtn"
          onMouseDown={e => {
            // Prevents mouse click focus ring
            e.preventDefault();
          }}
          onClick={() => {
            setVideoConference(NONE);
            setOtherUrl('');
            setZoomUrl(null);
          }}
          disabled={disabled}
        >
          <Trash2 size={24} className="c-midnight-50 mr-8" />
        </button>
        <input
          name="zoomVideoConferenceUrl"
          type="url"
          className="NewInput NewInput--white NewInput--fw"
          placeholder={t('in_person_training.new_event.invitation_link')}
          value={zoomUrl}
          readOnly
          disabled={disabled}
        />
      </div>
    );
  };

  const getOtherUrlField = () => {
    if (videoConference !== OTHER) return <></>;

    return (
      <>
        {getRequiredText()}
        <input
          name="otherVideoConferenceUrl"
          aria-label="Other Video Conferencing Input"
          aria-required="true"
          type="url"
          className="NewInput NewInput--white NewInput--fw"
          onChange={event => setOtherUrl(event.target.value)}
          placeholder={t('in_person_training.new_event.invitation_link')}
          value={otherUrl}
        />
      </>
    );
  };

  return (
    <div className="EventSessionForm-field">
      <label htmlFor="videoConferenceOptions">
        {t('common.video_conference')}
      </label>
      {videoConference === ZOOM && isStartBeforeCurrent && (
        <div className="zoom-past-date-warning">
          <AlertCircle />{' '}
          {t('in_person_training.new_event.zoom_past_date_error')}
        </div>
      )}
      <RadioFieldGroup
        className="ml-12"
        name={`videoConferenceOptions-${sessionId}`}
        selectedValue={videoConference}
        onChange={handleTypeChange}
        disabled={disabled}
      >
        <RadioField value={NONE} disabled={!isTimeZoneSelected}>
          {t('in_person_training.new_event.no_video_conferencing')}
          <Text variant={TEXT_VARIANTS.BODY_LIGHT} component="div">
            <span className="fz-14">
              {t(
                'in_person_training.new_event.no_video_conferencing_description',
              )}
            </span>
          </Text>
        </RadioField>
        {getZoomOption()}
        <ZoomMeetingUrl />
        <RadioField value={OTHER} disabled={!isTimeZoneSelected}>
          {t('in_person_training.new_event.other_video_conferencing')}
          <Text variant={TEXT_VARIANTS.BODY_LIGHT} component="div">
            <span className="fz-14">
              {t(
                'in_person_training.new_event.other_video_conferencing_description',
              )}
            </span>
          </Text>
        </RadioField>
      </RadioFieldGroup>
      {videoConference === OTHER && getOtherUrlField()}
    </div>
  );
}

export default VideoConference;
